import { Controller } from '@hotwired/stimulus'
import { api } from '../../api'
import { SubmissionFormType } from '../../types/common'
import { StreamEvent, StreamEventDetail } from './stream-manager'
import SubmissionController, { SetStatusEvent } from './submission'

export type Action = 'edit' | 'new' | 'show' | 'update'

interface HandleDeleteEvent extends Event {
  detail: {
    submissionId: string
  }
}
export default class SubmissionContainerController extends Controller<HTMLElement> {
  declare actionValue: Action
  declare containerSessionIdValue: string
  declare idValue: string
  declare identityIdValue: string
  declare readonly hasSubmissionOutlet: boolean
  declare readonly statusTarget: HTMLInputElement
  declare readonly submissionOutlets: SubmissionController[]

  static outlets = ['submission']

  static targets = ['status']

  static values = {
    action: String,
    containerSessionId: String,
    id: String,
    identityId: String,
  }

  #submitting = false

  // Getters

  get formType(): SubmissionFormType {
    return this.hasSubmissionOutlet ? this.submissionOutlets.at(0)!.formTypeValue : 'NewBusinessSubmission'
  }

  get lockedSubmissions() {
    return this.hasSubmissionOutlet ? this.submissionOutlets.filter(submission => submission.isLocked) : []
  }

  get isEditing() {
    return this.actionValue === 'edit'
  }

  // Lifecycle methods

  connect() {
    this.dispatch('opened')
    //FIXME: submission-record might be more appropriate, but some rewiring is needed in the BE
    document.addEventListener('stream-manager:render-record', this.#handleSubmissionRecordStream)
  }

  disconnect() {
    document.removeEventListener('stream-manager:render-record', this.#handleSubmissionRecordStream)
  }

  // Public methods

  // The multi-submission drawer names its container session, which lets the
  // server clean up everything the drawer held in one request.
  async cleanup() {
    if (this.containerSessionIdValue) return this.#endContainerSession()

    await this.#endReviewSession()
  }

  handleDelete({ detail: { submissionId } }: HandleDeleteEvent) {
    const submission = this.submissionOutlets.find(submission => submission.idValue === submissionId)
    if (!submission) return

    // The Submission and its associated Lock have been deleted by this point.
    // Let's clear the `lockId` value to prevent the cleanup phase from
    // attempting to unlock the Submission as the drawer closes.
    submission.lockIdValue = ''
  }

  // Leaving the page. Downloading an attachment can fire beforeunload without
  // leaving, so skip that, as locking.js does.
  leave() {
    if (document.activeElement?.closest('[data-attachment]')) return

    this.cleanup()
  }

  setStatus(event: SetStatusEvent) {
    if (this.hasSubmissionOutlet) {
      for (const submission of this.submissionOutlets) {
        submission.setStatus(event)
      }
    }
  }

  submitEnded() {
    this.#submitting = false
  }

  submitStarted() {
    this.#submitting = true
  }

  // Private methods

  #endContainerSession() {
    // Submit deletes the locks itself; deleting them mid-save would break it.
    if (this.#submitting) return

    api.containerSessions
      .destroy({ id: this.containerSessionIdValue, submissionContainerId: this.idValue })
      .catch(() => {})
  }

  async #endReviewSession() {
    const results = await Promise.allSettled(this.lockedSubmissions.map(submission => submission.unlock()))

    if (results.some(result => result.status === 'rejected')) {
      console.error('Could not unlock all Submissions', { results })
    }
  }

  #handleSubmissionRecordStream = (event: StreamEvent) => {
    const { streamElement } = event.detail
    switch (streamElement.action) {
      case 'replace':
      case 'prepend':
        this.#submissionRecordAction(event.detail)
        break
      default:
      // No default by design
    }
  }

  #submissionRecordAction({ originalRender, streamElement, fragment, upstreamEvent }: StreamEventDetail) {
    this.#trackChanges(fragment)
    originalRender(streamElement)

    upstreamEvent.preventDefault()
  }

  #trackChanges(fragment: HTMLElement) {
    const changedElements = Array.from(fragment.querySelectorAll('.broadcasted-field'))

    changedElements.forEach(element => {
      const record = element.closest('.new-business-record')
      if (record) record.classList.add('animate-record')
    })
  }
}
